<script setup lang="ts">
import type {
  EditModeFieldProps,
  FieldEmits,
  FieldProps,
  GroundedFieldEmits,
} from '@/modules/Project/Fields/field-types'
import { getFieldClaims } from '@/modules/Project/Fields/utils/grounding'
import { useFieldStatusIndicator } from '@/modules/Project/useFieldStatusIndicator'
import { useTextField } from '@/modules/Project/useTextField'
import CaseMarkdownWrapper from '@/sharedComponents/CaseMarkdownWrapper.vue'
import MarkdownEditor from '@/sharedComponents/MarkdownEditor/MarkdownEditor.vue'
import MarkdownWithGroundingClaims from '@/sharedComponents/MarkdownWithGroundingClaims/MarkdownWithGroundingClaims.vue'
import { twMerge } from 'tailwind-merge'
import { computed, useTemplateRef } from 'vue'

type Props = FieldProps<'text'> & EditModeFieldProps
type Emits = FieldEmits<'text'> & { input: [value: string] } & GroundedFieldEmits

const props = defineProps<Props>()
defineEmits<Emits>()
defineOptions({ inheritAttrs: false })
const editable = useTemplateRef('editable')

const field = computed(() => props.field)
const { localValue } = useTextField(field)
const { isModelOutput } = useFieldStatusIndicator(field)

defineExpose({
  focus: () => editable.value?.focus(),
})

const claims = computed(() => {
  if (isModelOutput.value) {
    return getFieldClaims(field.value)
  }
  return []
})
</script>

<template>
  <CaseMarkdownWrapper>
    <!-- Editing a markdown with grounding claims would keep the claim markers while text is being edited -->
    <!-- but drop them when text is saved. For that reason, swap for a plain MD editor when we want to edit. -->
    <MarkdownWithGroundingClaims
      v-if="readonly && claims.length > 0"
      readonly
      v-bind="$attrs"
      :class="twMerge('rounded-corner-8 px-2 py-1', String($attrs.class || ''))"
      :value="localValue || ''"
      :claims="claims"
      :property-id="property.id"
      @focus="$emit('focus', $event)"
      @blur="$emit('blur', $event)"
      @save="$emit('submit', $event)"
      @show-source="$emit('show-source', $event)"
    />
    <MarkdownEditor
      v-else
      v-bind="$attrs"
      ref="editable"
      :class="twMerge('rounded-corner-8 px-2 py-1', String($attrs.class || ''))"
      :value="localValue || ''"
      @focus="$emit('focus', $event)"
      @blur="$emit('blur', $event)"
      @save="$emit('submit', $event)"
      @input="$emit('input', $event)"
      @change="$emit('change', $event)"
    />
  </CaseMarkdownWrapper>
</template>
